import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Result, Spin, Row, Col } from 'antd';
import { verifyInvitation } from '../../services/invitation.service';
import { STORAGE_KEYS } from '../../utils/constants';
import { LoginWrapper } from '../Login/LoginStyle';
import { ThemeButton } from '../Styles/Theme';

const VerifyInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link. Please check your email for the correct link.');
      setErrorCode('INVALID_TOKEN');
      return;
    }

    const verify = async () => {
      const result = await verifyInvitation(token);

      if (result.success) {
        // Store session token and user info
        localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, result.data.sessionToken);
        localStorage.setItem(STORAGE_KEYS.PENDING_USER, JSON.stringify(result.data.user));

        // Redirect directly to set-password page (replace history so back button won't work)
        navigate('/auth/np2w8q', { replace: true });
      } else {
        setStatus('error');
        setMessage(result.message);
        setErrorCode(result.data?.code || 'UNKNOWN_ERROR');
      }
    };

    verify();
  }, [token, navigate]);

  const getErrorTitle = () => {
    switch (errorCode) {
      case 'TOKEN_EXPIRED':
        return 'Link Expired';
      case 'ALREADY_ACTIVE':
        return 'Account Already Active';
      case 'ACCOUNT_SUSPENDED':
        return 'Account Suspended';
      case 'INVALID_TOKEN':
        return 'Invalid Invitation Link';
      default:
        return 'Verification Failed';
    }
  };

  const getErrorMessage = () => {
    switch (errorCode) {
      case 'TOKEN_EXPIRED':
        return 'This invitation link has expired. Please contact your administrator to receive a new invitation.';
      case 'ALREADY_ACTIVE':
        return 'Your account is already set up. You can login with your credentials.';
      case 'ACCOUNT_SUSPENDED':
        return 'Your account has been suspended. Please contact your administrator for assistance.';
      case 'INVALID_TOKEN':
        return 'This invitation link is invalid or has already been used. Please contact your administrator to receive a new invitation.';
      default:
        return message || 'Something went wrong. Please contact your administrator for assistance.';
    }
  };

  const getErrorStatus = () => {
    if (errorCode === 'TOKEN_EXPIRED') return 'warning';
    if (errorCode === 'ALREADY_ACTIVE') return 'info';
    return 'error';
  };

  // Show loading state with portal design
  if (status === 'loading') {
    return (
      <LoginWrapper>
        <Row gutter={[60, 16]}>
          <Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 10, order: 1 }} xl={{ span: 10, order: 1 }}>
            <div className='loginLogo'>
              <img src='/images/logo.svg' alt="CrowdVox" />
            </div>
            <div className='loginContainer'>
              <div style={{ textAlign: 'center', paddingTop: '60px' }}>
                <Spin size="large" />
                <h1 style={{ marginTop: '24px', fontSize: '24px' }}>Verifying Invitation...</h1>
                <p style={{ color: '#6F7B8C', fontSize: '14px' }}>Please wait while we verify your invitation link.</p>
              </div>
            </div>
          </Col>
          <Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 14, order: 1 }} xl={{ span: 14, order: 1 }}>
            <div className='loginBanner'>
              <img className='w-full h-full' src='/images/login-banner.png' alt='CrowdVox' />
            </div>
          </Col>
        </Row>
      </LoginWrapper>
    );
  }

  // Show error state with antd Result (like 404 page)
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f5f5f5'
    }}>
      <Result
        status={getErrorStatus()}
        title={getErrorTitle()}
        subTitle={getErrorMessage()}
        extra={
          <ThemeButton size="large" className="loginBtn" onClick={() => navigate('/login', { replace: true })}>
            Go to Login
          </ThemeButton>
        }
      />
    </div>
  );
};

export default VerifyInvitation;
